import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ExternalLink, Plus, Sparkles, Trash2 } from "lucide-react";
import { FormEvent, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { EmptyState, Field } from "../components/Fields";
import { Modal } from "../components/Modal";
import { apiRequest, numberOrNull, stringOrNull } from "../lib/api";
import { applyListingPreviewToForm, describeListingPreview } from "../lib/listings";
import type { ListingPreview, Workspace } from "../lib/types";

const statusOptions = [
  "Researching",
  "Tour Scheduled",
  "Offer Due",
  "Preparing Offer",
  "Under Contract",
  "Closed",
];

export function DashboardPage() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listingMessage, setListingMessage] = useState<string | null>(null);
  const propertyFormRef = useRef<HTMLFormElement>(null);

  const workspacesQuery = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => apiRequest<Workspace[]>("/api/workspaces"),
  });

  const createWorkspace = useMutation({
    mutationFn: (payload: Partial<Workspace> & { address: string }) =>
      apiRequest<Workspace>("/api/workspaces", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      setModalOpen(false);
    },
  });

  const patchWorkspace = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<Workspace> }) =>
      apiRequest<Workspace>(`/api/workspaces/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workspaces"] }),
  });

  const deleteWorkspace = useMutation({
    mutationFn: (id: number) =>
      apiRequest<void>(`/api/workspaces/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workspaces"] }),
  });

  const previewListing = useMutation({
    mutationFn: (url: string) =>
      apiRequest<ListingPreview>("/api/listings/preview", {
        method: "POST",
        body: JSON.stringify({ url }),
      }),
  });

  async function handlePreviewListing() {
    const form = propertyFormRef.current;
    const url = form?.elements.namedItem("listing_url");
    const listingUrl = url instanceof HTMLInputElement ? url.value.trim() : "";
    if (!form || !listingUrl) {
      setError("Paste a Redfin or Zillow URL first");
      return;
    }
    setError(null);
    setListingMessage(null);
    try {
      const preview = await previewListing.mutateAsync(listingUrl);
      applyListingPreviewToForm(form, preview);
      setListingMessage([describeListingPreview(preview), ...preview.warnings].join(" "));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not import listing");
    }
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const form = new FormData(event.currentTarget);
    const address = String(form.get("address") || "").trim();
    if (!address) {
      setError("Address is required");
      return;
    }
    try {
      await createWorkspace.mutateAsync({
        address,
        listing_url: stringOrNull(form.get("listing_url")),
        purchase_price: numberOrNull(form.get("purchase_price")),
        bedrooms: numberOrNull(form.get("bedrooms")),
        bathrooms: numberOrNull(form.get("bathrooms")),
        square_feet: numberOrNull(form.get("square_feet")),
        status: String(form.get("status") || "Researching"),
        offer_deadline: stringOrNull(form.get("offer_deadline")),
        personal_notes: stringOrNull(form.get("personal_notes")),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create property");
    }
  }

  const workspaces = workspacesQuery.data ?? [];

  return (
    <AppShell>
      <section className="page-heading">
        <div>
          <p className="eyebrow">Dashboard</p>
          <h1>Property workspaces</h1>
          <p>Keep each active home purchase organized from first tour through closing.</p>
        </div>
        <button className="button button-primary" type="button" onClick={() => setModalOpen(true)}>
          <Plus size={17} />
          Add property
        </button>
      </section>

      {workspacesQuery.isLoading ? <div className="screen-loader inline">Loading properties...</div> : null}

      {!workspacesQuery.isLoading && workspaces.length === 0 ? (
        <EmptyState
          title="No properties yet"
          body="Create a workspace for the next home you are considering seriously."
          action={
            <button className="button button-primary" type="button" onClick={() => setModalOpen(true)}>
              <Plus size={17} />
              Add property
            </button>
          }
        />
      ) : null}

      <div className="workspace-grid">
        {workspaces.map((workspace) => (
          <article className="workspace-card" key={workspace.id}>
            <Link to={`/app/workspaces/${workspace.id}`} className="workspace-card-main">
              <div>
                <span className="status-pill">{workspace.status}</span>
                <h2>{workspace.address}</h2>
              </div>
              <dl className="metric-row">
                <div>
                  <dt>Offer deadline</dt>
                  <dd>{workspace.offer_deadline || "Not set"}</dd>
                </div>
                <div>
                  <dt>List price</dt>
                  <dd>{formatMoney(workspace.purchase_price)}</dd>
                </div>
              </dl>
              {workspace.personal_notes ? <p>{workspace.personal_notes}</p> : null}
            </Link>
            <div className="card-actions">
              {workspace.listing_url ? (
                <a className="icon-button" href={workspace.listing_url} target="_blank" rel="noreferrer" aria-label="Open listing">
                  <ExternalLink size={17} />
                </a>
              ) : null}
              <button
                className="icon-button"
                type="button"
                onClick={() => patchWorkspace.mutate({ id: workspace.id, payload: { archived: true } })}
                aria-label="Archive property"
              >
                <Archive size={17} />
              </button>
              <button
                className="icon-button danger"
                type="button"
                onClick={() => {
                  if (window.confirm(`Delete ${workspace.address}?`)) {
                    deleteWorkspace.mutate(workspace.id);
                  }
                }}
                aria-label="Delete property"
              >
                <Trash2 size={17} />
              </button>
            </div>
          </article>
        ))}
      </div>

      <Modal
        title="Add property"
        description="Paste a listing URL or enter details manually. You can fill in the rest later."
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setListingMessage(null);
          setError(null);
        }}
      >
        <form ref={propertyFormRef} className="stack-form" onSubmit={handleCreate}>
          <Field label="Address">
            <input name="address" required placeholder="123 Main St, Portland, OR" />
          </Field>
          <div className="listing-import-row">
            <Field label="Listing URL">
              <input name="listing_url" type="url" placeholder="https://www.redfin.com/..." />
            </Field>
            <button
              className="button button-secondary"
              type="button"
              onClick={handlePreviewListing}
              disabled={previewListing.isPending}
            >
              <Sparkles size={17} />
              Fill from listing
            </button>
          </div>
          {listingMessage ? <p className="form-hint">{listingMessage}</p> : null}
          <div className="form-grid">
            <Field label="List price">
              <input name="purchase_price" type="number" min="0" step="1000" />
            </Field>
            <Field label="Bedrooms">
              <input name="bedrooms" type="number" min="0" step="1" />
            </Field>
            <Field label="Bathrooms">
              <input name="bathrooms" type="number" min="0" step="0.5" />
            </Field>
            <Field label="Square feet">
              <input name="square_feet" type="number" min="0" step="1" />
            </Field>
          </div>
          <div className="form-grid two">
            <Field label="Status">
              <select name="status" defaultValue="Researching">
                {statusOptions.map((status) => (
                  <option key={status}>{status}</option>
                ))}
              </select>
            </Field>
            <Field label="Offer deadline">
              <input name="offer_deadline" type="date" />
            </Field>
          </div>
          <Field label="Personal notes">
            <textarea name="personal_notes" rows={4} placeholder="What matters about this property?" />
          </Field>
          {error ? <p className="form-error">{error}</p> : null}
          <div className="form-actions">
            <button className="button button-secondary" type="button" onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button className="button button-primary" type="submit" disabled={createWorkspace.isPending}>
              Create workspace
            </button>
          </div>
        </form>
      </Modal>
    </AppShell>
  );
}

function formatMoney(value?: number | null) {
  if (value === null || value === undefined) return "Not set";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}
